'use client';

import { createContext, type PropsWithChildren, useContext } from 'react';
import { useProjectWorkspaceIdFragment } from '@frameio/graphql-client/generated';

type ProjectId = string;
type WorkspaceId = string;

export const IsolatedActiveWorkspaceIdContext = createContext<
  WorkspaceId | undefined
>(undefined);

export const useGetActiveWorkspaceIdByProjectId = (projectId: string) => {
  const workspaceId = useProjectWorkspaceIdFragment({
    from: {
      __typename: 'Project',
      id: projectId,
    },
  })?.data?.workspaceId;

  return { workspaceId };
};

export const useActiveWorkspaceId = () =>
  useContext(IsolatedActiveWorkspaceIdContext);

type IsolatedActiveWorkspaceContextProviderProps = {
  children: React.ReactNode;
  projectId: ProjectId;
  /**
   * Workspace id already known by the caller (e.g. resolved server-side in an
   * RSC layout). The `ProjectWorkspaceId` fragment below is a client cache read,
   * and on a cold cache — such as a hard load straight onto a project route,
   * where the RSC cache is not transferred — it resolves as `undefined` until
   * some other query happens to write `Project.workspaceId`. Consumers that gate
   * work on `useActiveWorkspaceId()` would stall for that whole window. Seeding
   * the fallback closes the gap while the fragment stays the source of truth, so
   * a project moving between workspaces still updates reactively.
   */
  fallbackWorkspaceId?: WorkspaceId;
};

export const IsolatedActiveWorkspaceIdContextProvider = ({
  projectId,
  fallbackWorkspaceId,
  children,
}: IsolatedActiveWorkspaceContextProviderProps) => {
  const { workspaceId } = useGetActiveWorkspaceIdByProjectId(projectId);
  return (
    <IsolatedActiveWorkspaceIdContext.Provider
      value={workspaceId ?? fallbackWorkspaceId}
    >
      {children}
    </IsolatedActiveWorkspaceIdContext.Provider>
  );
};

export const IsolatedFixedWorkspaceIdContextProvider = ({
  children,
  workspaceId,
}: PropsWithChildren<{ workspaceId?: string }>) => (
  <IsolatedActiveWorkspaceIdContext.Provider value={workspaceId}>
    <>{children}</>
  </IsolatedActiveWorkspaceIdContext.Provider>
);
